/**
 * @fileoverview Variant service for fetching and managing product variants.
 * @module lib/variantService
 *
 * Note: This service corresponds to the backend's VariantController (/api/variants).
 * "Variant" is the internal canonical model (dim_variant) used everywhere in the domain.
 */

import { z } from 'zod'
import api from './apiClient'

type PagedOutput<T> = {
  content: T[]
  page?: number | undefined
  size?: number | undefined
  totalElements?: number | undefined
  totalPages?: number | undefined
  first?: boolean | undefined
  last?: boolean | undefined
}

const pagedResponseSchema = <T extends z.ZodTypeAny>(
  item: T
): z.ZodType<PagedOutput<z.infer<T>>> =>
  z
    .object({
      content: z.array(item).default([]),
      page: z.union([z.number(), z.object({}).passthrough()]).optional(),
      size: z.union([z.number(), z.object({}).passthrough()]).optional(),
      totalElements: z.union([z.number(), z.object({}).passthrough()]).optional(),
      totalPages: z.union([z.number(), z.object({}).passthrough()]).optional(),
      first: z.union([z.boolean(), z.object({}).passthrough()]).optional(),
      last: z.union([z.boolean(), z.object({}).passthrough()]).optional(),
    })
    .passthrough() as unknown as z.ZodType<PagedOutput<z.infer<T>>>

export const variantParamsSchema = z.object({
  page: z.number().min(0).default(0),
  size: z.number().min(1).max(1000).default(20),
  sort: z.string().default('sku'),
  direction: z.enum(['asc', 'desc']).default('asc'),
})
export type VariantParams = z.infer<typeof variantParamsSchema>

const currencyCodeSchema = z
  .union([
    z
      .string()
      .nullable()
      .optional()
      .transform(v => (typeof v === 'string' ? v.toUpperCase().slice(0, 3) : v)),
    z
      .object({
        currencyCode: z.string(),
      })
      .transform(v => v.currencyCode),
  ])
  .nullable()
  .optional()

const variantBaseSchema = z
  .object({
    id: z.number(),
    sku: z.string().optional(),
    name: z.string().optional(),
    variantSkuKey: z.string().optional(),
    description: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    brand: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    originalCost: z.number().nullable().optional(),
    originalCurrency: currencyCodeSchema,
    reportingCost: z.number().nullable().optional(),
    reportingCurrency: currencyCodeSchema,
    costDate: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    productName: z.string().nullable().optional(),
    productId: z.string().nullable().optional(),
    profit: z.number().nullable().optional(),
    systemSku: z.boolean().nullable().optional(),
    archived: z.boolean().nullable().optional(),
    /** Discrete variant flag - when true, this is a hidden auto-created variant for simple products */
    isDiscreteVariant: z.boolean().nullable().optional(),
    // Auto-resolved staging entries (present after update operations that trigger auto-resolution)
    autoResolved: z
      .array(
        z.object({
          slug: z.string(),
          source: z.string(),
        })
      )
      .nullable()
      .optional(),
  })
  .passthrough()

export const variantSchema = variantBaseSchema.transform(data => {
  // Fallback for legacy backend or migration: use variantSkuKey if sku is missing
  const sku = data.sku ?? data.variantSkuKey ?? ''
  // Fallback for name: use sku if name is missing (though name should be present now)
  const name = data.name ?? sku

  return { ...data, sku, name }
})

/**
 * Variant type representing an atomic sellable unit (dim_variant in backend).
 * This is the internal canonical model used everywhere in the domain.
 */
export type Variant = z.infer<typeof variantSchema>

/**
 * @deprecated Use `Variant` type instead. This alias exists for backward compatibility.
 */
export type SKU = Variant

/**
 * @deprecated Use `variantSchema` instead. This alias exists for backward compatibility.
 */
export const skuSchema = variantSchema

/**
 * @deprecated Use `variantParamsSchema` instead. This alias exists for backward compatibility.
 */
export const skuParamsSchema = variantParamsSchema

/**
 * @deprecated Use `VariantParams` instead. This alias exists for backward compatibility.
 */
export type SkuParams = VariantParams

export async function getAllVariants(): Promise<Variant[]> {
  const response = await api.get('/variants', {
    params: { page: 0, size: 1000, sort: 'sku', direction: 'asc', includeSystem: false },
  })
  const data = pagedResponseSchema(variantSchema).parse(response.data)
  return data.content
}

/**
 * @deprecated Use `getAllVariants` instead. This alias exists for backward compatibility.
 */
export const getAllSkus = getAllVariants

export async function getVariant(id: number | string): Promise<Variant> {
  const response = await api.get(`/variants/${id}`)
  return variantSchema.parse(response.data)
}

/**
 * @deprecated Use `getVariant` instead. This alias exists for backward compatibility.
 */
export const getSku = getVariant

/**
 * Remove null/undefined keys so we only send concrete updates.
 * Some backends treat null as forbidden for non-nullable columns and may return 403.
 */
function omitNullish<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined) {
      out[k as keyof T] = v as T[keyof T]
    }
  }
  return out
}

export type UpdateVariantPayload = Partial<
  Omit<Variant, 'id' | 'profit' | 'reportingCost' | 'reportingCurrency'>
>

/**
 * @deprecated Use `UpdateVariantPayload` instead. This alias exists for backward compatibility.
 */
export type UpdateSkuPayload = UpdateVariantPayload

export async function updateVariant(
  id: number | string,
  variantData: UpdateVariantPayload
): Promise<Variant> {
  const payload = omitNullish(variantData)
  console.debug('[variantService] PUT /variants/%s payload:', id, payload)
  const response = await api.put(`/variants/${id}`, payload)
  return variantSchema.parse(response.data)
}

/**
 * @deprecated Use `updateVariant` instead. This alias exists for backward compatibility.
 */
export const updateSku = updateVariant

export async function deleteVariant(
  variantId: number | string,
  targetVariantId?: number
): Promise<void> {
  await api.delete(`/variants/${variantId}`, {
    params: targetVariantId ? { targetVariantId } : undefined,
  })
}

/**
 * @deprecated Use `deleteVariant` instead. This alias exists for backward compatibility.
 */
export const deleteSku = deleteVariant

/**
 * Fetch paginated list of variants with sorting.
 *
 * Defaults: page 0, size 50, sort 'sku', direction 'asc'.
 * Returns the parsed paginated object, not just content.
 *
 * @param params Partial pagination/sort params
 * @returns Parsed paginated response of variants
 *
 * @example
 * ```ts
 * const page = await getVariants({ page: 1, size: 25, sort: 'name', direction: 'desc' })
 * console.log(page.content.length, page.totalElements)
 * ```
 */
export async function getVariants(params: Partial<VariantParams>): Promise<PagedOutput<Variant>> {
  const parsed = variantParamsSchema.partial().parse(params ?? {})
  // Map 'sku' sort to 'sku' (backend should support this now)
  // Map 'name' sort to 'name'
  // If legacy 'variantSkuKey' is used, map to 'sku' if needed, or keep as is.
  let sortField = parsed.sort ?? 'sku'

  if (sortField === 'variantSkuKey') sortField = 'sku'

  const response = await api.get('/variants', {
    params: {
      page: parsed.page ?? 0,
      size: parsed.size ?? 50,
      sort: sortField,
      direction: parsed.direction ?? 'asc',
      includeSystem: false,
    },
  })
  const data = pagedResponseSchema(variantSchema).parse(response.data)
  return data
}

/**
 * @deprecated Use `getVariants` instead. This alias exists for backward compatibility.
 */
export const getSkus = getVariants

export const variantImageSchema = z.object({
  id: z.number(),
  name: z.string(),
  imageUrl: z.string().nullable(),
})
export type VariantImage = z.infer<typeof variantImageSchema>

/**
 * @deprecated Use `VariantImage` instead. This alias exists for backward compatibility.
 */
export type SkuImage = VariantImage

/**
 * @deprecated Use `variantImageSchema` instead. This alias exists for backward compatibility.
 */
export const skuImageSchema = variantImageSchema

/**
 * Fetch all variant images from the backend.
 *
 * @returns Array of variant image data
 * @example
 * ```ts
 * const variantImages = await getVariantImages()
 * const imageMap = new Map(variantImages.map(v => [v.name, v.imageUrl]))
 * ```
 */
export async function getVariantImages(): Promise<VariantImage[]> {
  const response = await api.get('/variants/images')
  const data = z.array(variantImageSchema).parse(response.data)
  return data
}

/**
 * @deprecated Use `getVariantImages` instead. This alias exists for backward compatibility.
 */
export const getSkuImages = getVariantImages
